import type {
	ICellRendererParams,
	IRowNode,
	ValueFormatterParams,
} from 'ag-grid-community';

import { dataDate } from '@/features/dates/date-helpers';
import { RoomTypeRateTooltip } from '../components/room-type-rate-tooltip/room-type-rate-tooltip';
import {
	formattedCurrency,
	twoDecimalRateFormatter,
} from '../tableCustomCellRender';
import type { ColumnObj, SourceColumn } from '../useDashboardColumns';
import type { RecReviewData } from '../useDashboardController';
import {
	ColumnSources,
	DEFAULT_COL_WIDTH,
	type RecReviewDataWithRoomTypes,
} from './types';

export const liveDataComparator: (
	fieldName: keyof RecReviewData,
) => ColumnObj['comparator'] =
	(fieldName) => (liveValA, liveValB, nodeA, nodeB, isDescending) => {
		const rateA = liveValA ?? nodeA.data?.[fieldName];
		const rateB = liveValB ?? nodeB.data?.[fieldName];

		if (rateA === null) {
			return isDescending ? -1 : 1;
		}
		return rateA - rateB;
	};

export const createDynamicRoomTypeRatesColumns = (
	roomTypes:
		| Array<{ room_category: string; category_code: string }>
		| null
		| undefined,
	locale?: string,
	currencyCode?: string,
): SourceColumn[] => {
	const columns: SourceColumn[] = [];

	if (!roomTypes) return columns;

	roomTypes.forEach(({ room_category, category_code }) => {
		const sanitizedFieldName = room_category.replace(/[^a-zA-Z0-9]/g, '_');

		columns.push({
			headerName: category_code,
			field: `roomTypeRate_${sanitizedFieldName}`,
			source: ColumnSources.ROOM_TYPE_RATES,
			headerTooltip: `Recommended rate for ${category_code} (${room_category})`,
			valueFormatter: (
				params: ValueFormatterParams<RecReviewDataWithRoomTypes>,
			) => {
				const roomTypeRate = params.data?.roomTypeRates?.find(
					(rate) =>
						rate.room_category_id === room_category &&
						dataDate(rate.stay_date) === dataDate(params.data?.stay_date),
				);

				if (!roomTypeRate?.rec_rate) return '';

				return formattedCurrency(
					roomTypeRate.rec_rate.toString(),
					locale || 'en-US',
					currencyCode || 'USD',
					true,
					twoDecimalRateFormatter,
				);
			},
			cellRenderer: (
				params: ICellRendererParams<RecReviewDataWithRoomTypes>,
			) => {
				const roomTypeRate = params.data?.roomTypeRates?.find(
					(rate) =>
						rate.room_category_id === room_category &&
						dataDate(rate.stay_date) === dataDate(params.data?.stay_date),
				);

				if (!roomTypeRate?.rec_rate) return '';

				return (
					<RoomTypeRateTooltip
						roomType={category_code}
						rate={roomTypeRate.rec_rate}
						stayDate={params.data?.stay_date || ''}
						lastUpdated={roomTypeRate.updated_at || ''}
					>
						<div style={{ color: '#2e7d32' }}>
							{formattedCurrency(
								roomTypeRate.rec_rate.toString(),
								locale || 'en-US',
								currencyCode || 'USD',
								true,
								twoDecimalRateFormatter,
							)}
						</div>
					</RoomTypeRateTooltip>
				);
			},
			comparator: (_valueA: undefined, _valueB: undefined, nodeA, nodeB) => {
				const getRoomTypeRate = (
					node: IRowNode<RecReviewDataWithRoomTypes>,
				) => {
					const roomTypeRate = node.data?.roomTypeRates?.find(
						(rate: any) =>
							rate.room_category_id === room_category &&
							rate.stay_date === node.data?.stay_date,
					);
					return roomTypeRate?.rec_rate ?? 0;
				};

				const rateA = getRoomTypeRate(nodeA);
				const rateB = getRoomTypeRate(nodeB);

				return rateA - rateB;
			},
			width: DEFAULT_COL_WIDTH + 10,
		});
	});

	return columns;
};
